import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { Button, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateClientAssignedAgents } from "../../../common/api/queries";
import { TableSizes } from "../../../common/constants";
import { Permission } from "../../../common/security/enums";
import { AgentTypeTagWithName } from "../../agent/components/tags/AgentTypeTagWithName";
import { AgentList } from "../../agent/types";
import { formatAgentIdNumber } from "../../agent/utils";
import { useCurrentAgentAccount, useHasPermissions, useIsCurrentAgentTypePlus } from "../../auth/hooks";
import { Client } from "../types";
import { UpdateAssignedAgentsModalForm } from "./forms/UpdateAssignedAgentsModalForm";

type Props = {
  client: Client;
};

export const ClientAssignedAgents = ({ client }: Props) => {
  const { t } = useTranslation();

  const [isOpenAssignedAgentsModal, setIsOpenAssignedAgentsModal] = useState(false);

  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const agentUserAccount = useCurrentAgentAccount();
  const hasClientAssignedAgentsChangePermission = useHasPermissions(Permission.CLIENT_ASSIGNED_AGENTS_CHANGE);
  const hasClientUpdatePermission = useHasPermissions(Permission.CLIENT_UPDATE);

  const updateClientAssignedAgents = useUpdateClientAssignedAgents();

  const canChangeAssignedAgents =
    (hasClientUpdatePermission &&
      !!agentUserAccount?.agent.id &&
      agentUserAccount.agent.id === (client.managedBy?.id ?? client.managedById)) ||
    hasClientAssignedAgentsChangePermission;

  const columns = useMemo((): ColumnsType<AgentList> => {
    const items: ColumnsType<AgentList> = [
      {
        key: "name",
        title: t("agent.attrs.aggregatedName"),
        render: (_, record) => <AgentTypeTagWithName agent={record} showRepresentatives hideIdNumber />,
      },
      {
        key: "idNumber",
        title: t("agent.attrs.idNumber"),
        width: 150,
        render: (_, record) => formatAgentIdNumber(record),
      },
      {
        key: "commissionsSettingsLevel",
        title: t("agent.helpers.commissionsSettingsLevel"),
        width: 180,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.commissionsSettings.level?.code}</Ellipsis>,
      },
      {
        key: "commissionsSettingsRank",
        title: t("agent.helpers.commissionsSettingsRank"),
        width: 180,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.commissionsSettings.rank?.name}</Ellipsis>,
      },
      {
        key: "status",
        title: (
          <div>
            <div>
              {isTopAgentPlus ? t("agent.helpers.agentStatusSFAPlusLabel") : t("agent.enums.agentStatus._label")}
            </div>
            {isTopAgentPlus ? <div>{t("agent.helpers.agentSecondaryStatusSFAPlusLabel")}</div> : undefined}
          </div>
        ),
        width: 250,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <>
            <Ellipsis>{t(`agent.enums.agentStatus.${record.status}`)}</Ellipsis>

            {isTopAgentPlus && record.secondaryStatus ? (
              <>
                <br />
                <Ellipsis>{t(`agent.enums.agentSecondaryStatus.${record.secondaryStatus}`)}</Ellipsis>
              </>
            ) : undefined}
          </>
        ),
      },
    ];

    if (canChangeAssignedAgents) {
      items.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 110,
        render: (_, record) => (
          <Popconfirm
            styles={{ root: { width: 400 } }}
            title={t("client.helpers.removeAssignedAgentConfirm")}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() =>
              updateClientAssignedAgents.mutate({
                id: client.id,
                object: {
                  assignedAgentIds: (client.assignedAgents ?? [])
                    .filter((agent) => agent.id !== record.id)
                    .map((agent) => agent.id),
                  optimisticLockVersion: client.optimisticLockVersion as number,
                },
              })
            }
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.remove")} />
          </Popconfirm>
        ),
      });
    }

    return items;
  }, [client, isTopAgentPlus, canChangeAssignedAgents]);

  return (
    <>
      <Table<AgentList>
        {...tableStandardProps()}
        dataSource={client.assignedAgents ?? []}
        scroll={{ x: TableSizes.MEDIUM }}
        columns={columns}
        pagination={{ ...paginationTableProps, placement: ["bottomEnd"] }}
      />

      {canChangeAssignedAgents && (
        <>
          <Button
            className="margin-top-medium"
            type="primary"
            icon={<AntIcon type="edit" />}
            onClick={() => setIsOpenAssignedAgentsModal(true)}
          >
            {t("common.edit")}
          </Button>

          <UpdateAssignedAgentsModalForm
            client={client}
            isOpen={isOpenAssignedAgentsModal}
            onCancel={() => setIsOpenAssignedAgentsModal(false)}
          />
        </>
      )}
    </>
  );
};
